import React, { useState, useEffect } from "react";
import Profile from "./_45_user_profile.js";
import YTDPL from "./_8_YTD_PL.js";
import DateRangeSelector from "./_53_date_range_selector.js";
import { useDateRange } from "./dateRangeContext.js";
import { useTagFilter } from "./TagFilterContext.js";
import { useDateFilter } from "./_40b_date_filter_context.js";
import GlobalTagSearch from "./_02_global_tag_search.js";
import CalendarSelector from "./_42_calendar_selector.js";
import BannerGroup from "./_117_banner_group.js";
import { useFullscreenMode } from "./_fullscreenUtility.js";

//const MODULE_NAME = "nav_top";

// Format date from YYYY-MM-DD to MMM DD, YYYY
function formatActiveDateTitle(dateStr) {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[Number(parts[1]) - 1];
    const day = parts[2];
    const year = parts[0];
    return `${month} ${day}, ${year}`;
}

/**
 * DateFilterBadge Component
 * Shows active date filter and allows clearing it.
 * Only displayed on pg_details and pg_tags screens.
 */
function DateFilterBadge({ screen }) {
    const { navigationDate, setNavigationDate } = useDateFilter();
    
    // Initialize badgeDate from sessionStorage first, then sync with context
    const [badgeDate, setBadgeDate] = useState(() => {
        return sessionStorage.getItem("navigationDate") || navigationDate || "";
    });

    // Get latest_date from sessionStorage
    const latestDate = sessionStorage.getItem("latest_date") || "";

    // Listen for custom event to update badge immediately when cards are clicked
    useEffect(() => {
        const handleDateChange = (e) => {
            setBadgeDate(e.detail.date);
        };
        window.addEventListener("navigationDateChanged", handleDateChange);
        return () => window.removeEventListener("navigationDateChanged", handleDateChange);
    }, []);

    // Also sync with context for when calendar selector is used
    useEffect(() => {
        if (navigationDate) {
            setBadgeDate(navigationDate);
        }
    }, [navigationDate]);

    const dateToDisplay = badgeDate || navigationDate;

    // Hide badge if dateToDisplay matches latest_date or if dateToDisplay is empty
    if (!dateToDisplay || dateToDisplay === latestDate) return null;

    // Only show on pg_details and pg_tags
    if (screen !== "pg_details" && screen !== "pg_tags") return null;

    const handleBadgeClose = () => {
        // Get fallback date
        const latestDate = sessionStorage.getItem("latest_date") || "";

        // Remove the override from storage
        sessionStorage.removeItem("navigationDate");

        // Clear local badge state
        setBadgeDate("");

        // Revert context to latest_date. 
        // This triggers pg_details to refresh to current data.
        // For pg_tags, the useEffect in _47_trade_tag_table.js will see this matches latestDate 
        // and setExecTimeFilter("") automatically.
        setNavigationDate(latestDate);

        // Notify components immediately
        const event = new CustomEvent("navigationDateChanged", { detail: { date: latestDate } });
        window.dispatchEvent(event);
    };

    return (
        <div
            style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                background: "orange",
                color: "#222",
                padding: "8px 24px 8px 16px",
                borderRadius: 20,
                fontWeight: 600,
                fontSize: 15,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                marginTop: 8,
                minWidth: 180,
                justifyContent: "center"
            }}
        >
            <span>
                Date filter: {formatActiveDateTitle(dateToDisplay)}
            </span>
            <button
                onClick={handleBadgeClose}
                style={{
                    marginLeft: 16,
                    background: "transparent",
                    border: "none",
                    color: "#222",
                    fontWeight: 900,
                    fontSize: 18,
                    cursor: "pointer",
                    lineHeight: 1
                }}
                title="Clear date filter"
                aria-label="Clear date filter"
            >
                ×
            </button>
        </div>
    );
}

function TopHeader({ userId, screen, refreshKey, onNavigate }) {
    const { windowDays, setWindowDays } = useDateRange();
    const { globalTagFilter, setGlobalTagFilter } = useTagFilter();
    const { navigationDate, setNavigationDate } = useDateFilter();
    const fullscreenComponent = useFullscreenMode();

    const handleLogoClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onNavigate) {
            onNavigate("pg_monitor");
        }
    };

    const handleTagFilterChange = (filteredTags) => {
        setGlobalTagFilter(filteredTags);
    };

    const handleDateChange = (newDate) => {
        setNavigationDate(newDate);
        const event = new CustomEvent("navigationDateChanged", { detail: { date: newDate } });
        window.dispatchEvent(event);
    };

    // Hide entire nav when in fullscreen
    if (fullscreenComponent) {
        return null;
    }

    return (
        <header className="top-header" style={{ background: "#222", padding: "16px", position: "relative" }}>
            {/* DateFilterBadge - appears only on pg_details and pg_tags */}
            <DateFilterBadge screen={screen} />

            <div
                className="top-header-content"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 16px"
                }}
            >
                {/* Left section: logo, nav, profile, date range, tag search */}
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <img
                        src="images/logo/logo.png"
                        alt="Logo"
                        onClick={handleLogoClick}
                        title="Navigate to monitor"
                        style={{
                            height: "40px",
                            width: "auto",
                            objectFit: "contain",
                            borderRadius: "8px",
                            marginRight: "16px",
                            cursor: "pointer"
                        }}
                    />

                    {/* Left section: here */}
                    <Profile userId={userId} screen={screen} refreshKey={refreshKey} />
                    <DateRangeSelector
                        value={windowDays}
                        onDateChange={setWindowDays}
                        hidden={screen === "pg_details" || screen === "pg_upload" || screen === "pg_compare" || screen === "_38_research_pg" || screen === "pg_monitor" || screen === "pg_options" || screen === "pg_tags"}
                    />
                    {/* ********************** Global tag search *****************/}

                    <div style={{ display: "flex", alignItems: "center" }}>
                        <GlobalTagSearch
                            onTagSelect={(tag) => console.log("Tag selected:", tag)}
                            onFilterChange={handleTagFilterChange}
                            limit={15}
                            hidden={screen === "pg_patterns" || screen === "pg_tags" || screen === "pg_details" || screen === "_38_research_pg" || screen === "pg_monitor" || screen === "pg_options"}
                            style={{ marginBottom: 0 }}
                        />
                    </div>
                    {/* ********************** Calendar *****************/}

                    {(screen === "pg_details" || screen === "pg_tags") && (
                        <CalendarSelector
                            activeDate={navigationDate}
                            onDateChange={handleDateChange}
                        />
                    )}
                </div>
                {/* YTDPL on the right */}
                <YTDPL userId={userId} screen={screen} refreshKey={refreshKey} />
            </div>
            <div style={{ marginTop: 0 }}>
                <BannerGroup />
            </div>
        </header>
    );
}

export default TopHeader;